import * as THREE from "three";
import GUI from "lil-gui";
import { renderStarfield, applyStarsParallax, starsConfig } from "./stars.js";

// ────────────────────────────────────────────────────────────────────────────
// CONFIG — every knob in one place. Edit and reload to iterate.
// ────────────────────────────────────────────────────────────────────────────
const config = {
  // ── Birth event ────────────────────────────────────────────────────────
  birthDateUTC: "1995-06-24T13:00:00Z", // 10:00 AM GMT-3 = 13:00 UTC
  observerLat: -24.9555,                // Cascavel, Paraná, Brazil
  observerLon: -53.4552,

  // ── Astronomy constants ────────────────────────────────────────────────
  // A known new moon, used as the anchor for phase calculation.
  refNewMoonJD: 2451550.26,             // 2000-01-06 18:14 UTC
  synodicMonth: 29.530588853,           // mean lunar cycle in days

  // ── Camera ─────────────────────────────────────────────────────────────
  // Sizing rules for the moon, evaluated against the current viewport:
  //   1. Width constraint — moon diameter ≤ moonScreenFraction × viewport width
  //      (uses moonScreenFractionMobile when viewport width < mobileBreakpoint)
  //   2. Height constraint — moon diameter ≤ moonMaxHeightFraction × viewport height
  // Both must hold, so the tighter constraint wins (kicks in on widescreen).
  moonScreenFraction: 1 / 2,            // desktop: moon diameter / viewport width
  moonScreenFractionMobile: 2 / 3,      // mobile : moon diameter / viewport width
  moonMaxHeightFraction: 0.66,          // moon diameter / viewport height (cap)
  mobileBreakpoint: 768,                // viewport width (px) below which mobile applies
  fov: 28,                              // lower = flatter / more telephoto feel

  // ── Compact transition (scroll past hero → memory pages) ───────────────
  // As the user scrolls from the hero into the first memory section, the
  // moon shrinks and shifts upward to leave room for the memory cards.
  // Linearly interpolated over the 0 → 100vh scroll range; held at the
  // compact values for any further scroll. Desktop / mobile chosen by the
  // same `mobileBreakpoint` used for sizing.
  compactScale: 0.5,                    // desktop: 1 = unchanged; <1 = smaller
  compactScaleMobile: 0.80,             // mobile : usually a bit larger
  compactYOffsetVh: -22,                // desktop: negative = move moon UP
  compactYOffsetVhMobile: -28,          // mobile : usually a bit less aggressive

  // ── Lighting ───────────────────────────────────────────────────────────
  sunIntensity: 3.4,
  ambientIntensity: 0.004,              // dark side stays nearly black for stark contrast

  // ── Surface ────────────────────────────────────────────────────────────
  sphereSegments: 128,                  // sphere geometry resolution
  // Crater emphasis. Three.js treats the bumpMap's luminance as a heightmap
  // and shades accordingly — higher bumpScale = deeper-looking craters.
  // For the stylized look push to 0.15–0.30; subtle realism sits around 0.02.
  bumpScale: 0.9,

  // ── Stylization ────────────────────────────────────────────────────────
  // Multiplied with the texture color. 0xffffff = natural moon (no tint).
  // Try 0xd4b14a (gold leaf), 0xc8a04a (antique brass), 0xb8c5d6 (cool blue moon).
  moonTint: 0xfffbeb,
  // Procedural grain layered on top of the lit surface.
  grainScale: 1000.0,                    // higher = finer speckle
  grainAmount: 0.1,                     // 0 disables; 0.6+ gets pretty noisy
  // Final contrast curve. >1 darkens shadows + brightens highs.
  contrast: 1.2,

  // ── Observer orientation ───────────────────────────────────────────────
  // Rotates the rendered moon around the line of sight to match how it would
  // appear from the observer's hemisphere. 0 = standard north-up, 180 = flipped.
  // Set to null to derive automatically from observerLat.
  manualTiltDeg: null,

  // ── Timeline ───────────────────────────────────────────────────────────
  // Vertical height (in vh) of each inter-node section. Higher = more scroll
  // between snap points = slower phase change between adjacent memories.
  sectionHeightVH: 200,
};

// ────────────────────────────────────────────────────────────────────────────
// TIMELINE — chronological list of memories. The first entry is the birth
// moment (rendered by the hero); the rest get their own scroll-snapped
// sections. Edit dates / descriptions / image paths here.
// ────────────────────────────────────────────────────────────────────────────
const TIMELINE = [
  {
    date: "1995-06-24T13:00:00Z",
    description: ["This was the moon the day you became a mother."],
    images: [],
  },
  {
    date: "1995-06-24T13:00:00Z",
    description: ["I had the honor to be the first to call you mom."],
    images: [
      "/static/images/me_1.jpg",
      "/static/images/me_2.jpg"
    ],
  },
  {
    date: "1999-10-04T15:00:00Z",
    description: ["Then I shared that honor with my little brother."],
    images: [
      "/static/images/daniel_1.jpg",
      "/static/images/daniel_2.jpg",
      "/static/images/daniel_3.jpg"
    ],
  },
  {
    date: "2011-03-05T15:00:00Z",
    description: ["Some time later, your third son joined the family."],
    images: [
      "/static/images/tobie_1.jpg",
      "/static/images/tobie_2.jpg",
      "/static/images/tobie_3.jpg",
      "/static/images/tobie_4.jpg"
    ],
  },
  {
    date: "2025-12-25T15:00:00Z",
    description: ["Thanks for being our mom."],
    images: [
      "/static/images/sons_1.jpg",
      "/static/images/sons_2.png",
      "/static/images/sons_3.jpg"
    ],
  },
  {
    date: new Date().toISOString().slice(0, 19) + "Z",
    description: ["We love you!", "Happy Mother's Day!"],
    images: ["/static/images/us_1.jpg"]
  },
];

// ────────────────────────────────────────────────────────────────────────────
// COLLAGE LAYOUTS — per (viewport, image-count). Each entry is an array of
// {x, y, w, h} in PERCENTAGES of the collage container. Designed to feel like
// a casual scrapbook arrangement; tweak freely.
// ────────────────────────────────────────────────────────────────────────────
const COLLAGE_LAYOUTS = {
  desktop: {
    1: [{ x: 25, y: 0,  w: 50, h: 100 }],
    2: [
      { x: 4,  y: 8,  w: 48, h: 84 },
      { x: 48, y: 8,  w: 48, h: 84 },
    ],
    3: [
      { x: 0,  y: 10, w: 38, h: 80 },
      { x: 31, y: 0,  w: 38, h: 100 },
      { x: 62, y: 10, w: 38, h: 80 },
    ],
    4: [
      { x: 0,  y: 5,  w: 28, h: 85 },
      { x: 24, y: 12, w: 28, h: 85 },
      { x: 48, y: 5,  w: 28, h: 85 },
      { x: 72, y: 12, w: 28, h: 85 },
    ],
  },
  mobile: {
    1: [{ x: 8,  y: 5,  w: 84, h: 90 }],
    2: [
      { x: 4,  y: 4,  w: 70, h: 56 },
      { x: 26, y: 45, w: 70, h: 56 },
    ],
    3: [
      { x: 0,  y: 4,  w: 60, h: 38 },
      { x: 36, y: 30, w: 60, h: 38 },
      { x: 6,  y: 60, w: 60, h: 38 },
    ],
    4: [
      { x: 4,  y: 4,  w: 50, h: 44 },
      { x: 46, y: 6,  w: 50, h: 44 },
      { x: 4,  y: 52, w: 50, h: 44 },
      { x: 46, y: 50, w: 50, h: 44 },
    ],
  },
};
// ────────────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────────

// ── Astronomy ──────────────────────────────────────────────────────────────

function toJulianDate(date) {
  return date.getTime() / 86_400_000 + 2_440_587.5;
}

function moonPhaseFraction(date) {
  const jd = toJulianDate(date);
  const phases = (jd - config.refNewMoonJD) / config.synodicMonth;
  let frac = phases - Math.floor(phases);
  if (frac < 0) frac += 1;
  return frac; // 0 = new, 0.25 = first quarter, 0.5 = full, 0.75 = last quarter
}

function illuminationFromFraction(frac) {
  return (1 - Math.cos(2 * Math.PI * frac)) / 2;
}

function phaseName(frac) {
  if (frac < 0.03 || frac > 0.97) return "New Moon";
  if (frac < 0.22) return "Waxing Crescent";
  if (frac < 0.28) return "First Quarter";
  if (frac < 0.47) return "Waxing Gibbous";
  if (frac < 0.53) return "Full Moon";
  if (frac < 0.72) return "Waning Gibbous";
  if (frac < 0.78) return "Last Quarter";
  return "Waning Crescent";
}

// Sun direction in world space, given phase fraction.
// Convention: camera at -Z looking at +Z, lunar north at +Y.
//   new (0)         → sun behind moon  (away from camera)  → -Z
//   first qtr (¼)   → sun to ecliptic east                 → +X (lights right)
//   full (½)        → sun behind camera                    → +Z
//   last qtr (¾)    → sun to ecliptic west                 → -X (lights left)
function sunDirectionFromPhase(frac) {
  const theta = 2 * Math.PI * frac;
  return new THREE.Vector3(Math.sin(theta), 0, -Math.cos(theta)).normalize();
}

// Simple latitude-driven roll: northern observers see the moon "north up",
// southern observers see it flipped. A future pass can replace this with
// the actual parallactic angle for the observer at the moment of birth.
function tiltDegFromLatitude(lat) {
  return lat < 0 ? 180 : 0;
}

// Camera distance that satisfies BOTH the width and height constraints.
// Width is mobile-aware. Whichever constraint is tighter wins (larger d
// = smaller moon).
//
// Derivation per axis:
//   horizontal FOV  hfov = 2·atan(aspect · tan(vfov/2))
//   moon angular diam  α = 2·asin(1/d)
//   width  : α = wFrac · hfov  →  d = 1 / sin(wFrac · hfov / 2)
//   height : α = hFrac · vfov  →  d = 1 / sin(hFrac · vfov / 2)
function cameraDistanceForViewport(fovDeg, width, height) {
  const aspect = width / height;
  const isMobile = width < config.mobileBreakpoint;
  const wFrac = isMobile ? config.moonScreenFractionMobile : config.moonScreenFraction;
  const hFrac = config.moonMaxHeightFraction;

  const vfov = THREE.MathUtils.degToRad(fovDeg);
  const hfov = 2 * Math.atan(aspect * Math.tan(vfov / 2));

  const dWidth = 1 / Math.sin((wFrac * hfov) / 2);
  const dHeight = 1 / Math.sin((hFrac * vfov) / 2);
  return Math.max(dWidth, dHeight);
}

// ── Three.js scene ─────────────────────────────────────────────────────────

function buildScene(container) {
  const width = container.clientWidth;
  const height = container.clientHeight;

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();

  const aspect = width / height;
  const camera = new THREE.PerspectiveCamera(config.fov, aspect, 0.1, 100);
  const distance = cameraDistanceForViewport(config.fov, width, height);
  camera.position.set(0, 0, distance);
  camera.lookAt(0, 0, 0);

  const loader = new THREE.TextureLoader();
  const colorMap = loader.load("./textures/moon_color.jpg", () => {
    // Wait one rendered frame so the moon is on screen with its texture
    // before fading out the loading overlay.
    requestAnimationFrame(() => setTimeout(hideLoadingOverlay, 200));
  });
  colorMap.colorSpace = THREE.SRGBColorSpace;
  // Bump map: same texture, but read as linear luminance, not sRGB color.
  const bumpMap = loader.load("./textures/moon_color.jpg");

  const geometry = new THREE.SphereGeometry(1, config.sphereSegments, config.sphereSegments);
  const material = new THREE.MeshStandardMaterial({
    map: colorMap,
    bumpMap: bumpMap,
    bumpScale: config.bumpScale,
    color: new THREE.Color(config.moonTint),
    roughness: 1.0,
    metalness: 0.0,
  });

  // Stylized grain + contrast injected into the standard fragment shader.
  // We keep PBR lighting and bump-mapping intact, then post-process the
  // outgoing color: multiply by procedural noise, then apply a power curve.
  const styleUniforms = {
    uGrainScale: { value: config.grainScale },
    uGrainAmount: { value: config.grainAmount },
    uContrast: { value: config.contrast },
  };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uGrainScale = styleUniforms.uGrainScale;
    shader.uniforms.uGrainAmount = styleUniforms.uGrainAmount;
    shader.uniforms.uContrast = styleUniforms.uContrast;

    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <common>",
      `#include <common>
       uniform float uGrainScale;
       uniform float uGrainAmount;
       uniform float uContrast;
       float momgiftHash(vec2 p) {
         return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
       }
       float momgiftNoise(vec2 p) {
         vec2 i = floor(p);
         vec2 f = fract(p);
         f = f * f * (3.0 - 2.0 * f);
         return mix(
           mix(momgiftHash(i),                momgiftHash(i + vec2(1.0, 0.0)), f.x),
           mix(momgiftHash(i + vec2(0.0,1.0)),momgiftHash(i + vec2(1.0, 1.0)), f.x),
           f.y
         );
       }`,
    );

    // Patch the dithering chunk (runs just before final tone mapping) to
    // inject grain and contrast. This works whether or not dithering is on.
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <dithering_fragment>",
      `// Two-octave UV-based noise so grain sits ON the surface.
       float n = momgiftNoise(vMapUv * uGrainScale)
               + 0.5 * momgiftNoise(vMapUv * uGrainScale * 2.5);
       n = n / 1.5;
       float grain = mix(1.0, n * 2.0, uGrainAmount);
       gl_FragColor.rgb *= grain;
       gl_FragColor.rgb = pow(max(gl_FragColor.rgb, 0.0), vec3(uContrast));
       #include <dithering_fragment>`,
    );
  };
  material.userData.styleUniforms = styleUniforms;

  const moon = new THREE.Mesh(geometry, material);
  scene.add(moon);

  // Sun light — positioned by phase
  const sun = new THREE.DirectionalLight(0xfff4dc, config.sunIntensity);
  scene.add(sun);
  scene.add(sun.target);

  // Faint earthshine so the dark side isn't pure black
  const ambient = new THREE.AmbientLight(0x6b7aa8, config.ambientIntensity);
  scene.add(ambient);

  return { renderer, scene, camera, moon, sun };
}

// ── Per-node ambient tint ──────────────────────────────────────────────────
// Subtle color shifts as you move through the timeline. One color per
// TIMELINE entry; CSS transition on .ambient-tint smooths the swap.
const NODE_TINTS = [
  "rgba(60, 75, 130, 0.18)",   // 0: hero — cool indigo (night sky)
  "rgba(170, 130, 110, 0.14)", // 1: warm tan (infancy)
  "rgba(190, 155, 110, 0.14)", // 2: golden (sunny memories)
  "rgba(120, 150, 130, 0.14)", // 3: sage (growing up)
  "rgba(150, 125, 175, 0.14)", // 4: lavender (reflection)
  "rgba(195, 140, 150, 0.16)", // 5: rose gold (today, love)
];

function applyAmbientTint() {
  const idx = Math.max(0, Math.min(NODE_TINTS.length - 1, inferNodeIndexFromScroll()));
  const el = document.querySelector(".ambient-tint");
  if (el) el.style.backgroundColor = NODE_TINTS[idx] || NODE_TINTS[0];
}

// ── Image preload ──────────────────────────────────────────────────────────
// Kick off `Image()` fetches for the current and next nodes' images so the
// entrance animation never waits on bandwidth.
const preloadedImages = new Set();
function preloadImagesForNode(idx) {
  const node = TIMELINE[idx];
  if (!node || !node.images) return;
  for (const src of node.images) {
    if (!src || preloadedImages.has(src)) continue;
    preloadedImages.add(src);
    const img = new Image();
    img.src = src;
  }
}

function preloadAroundCurrent() {
  const idx = inferNodeIndexFromScroll();
  preloadImagesForNode(idx);
  preloadImagesForNode(idx + 1);
}

// Static (scroll-independent): hemisphere roll. Sun/phase are handled by
// applyDateState below since they change as the timeline progresses.
function applyStaticConfig({ camera }) {
  const tiltDeg =
    config.manualTiltDeg != null
      ? config.manualTiltDeg
      : tiltDegFromLatitude(config.observerLat);
  camera.rotation.z = THREE.MathUtils.degToRad(tiltDeg);
}

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

const MS_PER_DAY = 86_400_000;
const DAY_FMT = new Intl.NumberFormat("en-US"); // 11,275 with thousands separator

// Date-driven (changes with scroll): sun position + on-screen readout.
function applyDateState({ sun }, { date, phaseFrac }) {
  const sunDir = sunDirectionFromPhase(phaseFrac);
  sun.position.copy(sunDir.multiplyScalar(10));
  sun.target.position.set(0, 0, 0);
  sun.target.updateMatrixWorld();

  const illum = illuminationFromFraction(phaseFrac);
  const nameText = phaseName(phaseFrac);
  const illumText = Math.round(illum * 100) + "%";
  document.querySelectorAll(".phase-name").forEach((el) => { el.textContent = nameText; });
  document.querySelectorAll(".illumination").forEach((el) => { el.textContent = illumText; });
  document.getElementById("current-date").textContent = DATE_FMT.format(date);

  // Day count since the birth moment. Day 1 = the day she became a mother.
  const birthMs = new Date(config.birthDateUTC).getTime();
  const dayN = Math.max(1, Math.floor((date.getTime() - birthMs) / MS_PER_DAY) + 1);
  const dayEl = document.getElementById("days-counter");
  if (dayEl) dayEl.textContent = `Day ${DAY_FMT.format(dayN)}`;
}

// ── Scroll navigation (hop one node per gesture) ───────────────────────────

const HOP_COOLDOWN_MS = 600;       // minimum time between user-driven hops
const WHEEL_DEBOUNCE_MS = 180;     // trackpad inertia swallow window
const TOUCH_SWIPE_THRESHOLD = 30;  // px of vertical swipe to count as a hop

let currentNodeIndex = 0;
let lastHopTime = 0;
let wheelLocked = false;
let wheelEndTimer = null;

// Hidden 1px-wide element sized to 100svh. We read its offsetHeight to get
// the small-viewport height in pixels — a STABLE value that doesn't shift
// when iOS Safari toolbars collapse/expand. Used everywhere snap math
// needs a viewport height; window.innerHeight is dynamic and would let
// the user scroll-strand between snap points across toolbar transitions.
let svhProbeEl = null;
function getSmallVH() {
  if (!svhProbeEl) {
    svhProbeEl = document.createElement("div");
    svhProbeEl.setAttribute("aria-hidden", "true");
    svhProbeEl.style.cssText =
      "position:fixed;top:0;left:0;width:1px;height:100svh;visibility:hidden;pointer-events:none;";
    document.body.appendChild(svhProbeEl);
  }
  return svhProbeEl.offsetHeight || window.innerHeight;
}

function snapPositionForNode(idx) {
  if (idx <= 0) return 0;
  const vh = getSmallVH();
  const sectionPx = (vh * config.sectionHeightVH) / 100;
  return vh + (idx - 1) * sectionPx;
}

function inferNodeIndexFromScroll() {
  const sy = window.scrollY;
  const vh = getSmallVH();
  const sectionPx = (vh * config.sectionHeightVH) / 100;
  const last = TIMELINE.length - 1;
  if (sy <= vh / 2) return 0;
  const k = Math.round((sy - vh) / sectionPx) + 1;
  return Math.max(0, Math.min(last, k));
}

function hopToNode(targetIdx) {
  const last = TIMELINE.length - 1;
  targetIdx = Math.max(0, Math.min(last, targetIdx));
  currentNodeIndex = targetIdx;
  window.scrollTo({ top: snapPositionForNode(targetIdx), behavior: "smooth" });
}

function tryHop(direction) {
  const now = performance.now();
  if (now - lastHopTime < HOP_COOLDOWN_MS) return;
  lastHopTime = now;
  hopToNode(currentNodeIndex + direction);
}

function isInteractiveTarget(e) {
  if (!e.target || !e.target.closest) return false;
  return !!(
    e.target.closest(".lil-gui") ||
    e.target.closest(".image-modal")
  );
}

function bindNavigation() {
  currentNodeIndex = inferNodeIndexFromScroll();

  // Wheel: one hop per gesture, swallowing trackpad inertia via a debounce.
  window.addEventListener("wheel", (e) => {
    if (isInteractiveTarget(e)) return;
    if (modalIsOpen()) {
      // Block background scroll while the image modal is open. Closing
      // is via the × button, backdrop click, or Escape — not wheel.
      e.preventDefault();
      return;
    }
    e.preventDefault();

    clearTimeout(wheelEndTimer);
    wheelEndTimer = setTimeout(() => { wheelLocked = false; }, WHEEL_DEBOUNCE_MS);

    if (wheelLocked) return;
    const dir = Math.sign(e.deltaY);
    if (dir !== 0) {
      wheelLocked = true;
      tryHop(dir);
    }
  }, { passive: false });

  // Keyboard: arrow / page / space, plus home/end as bonuses.
  window.addEventListener("keydown", (e) => {
    if (modalIsOpen()) return; // Escape is handled by the modal itself
    const tag = document.activeElement && document.activeElement.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

    let dir = 0;
    switch (e.key) {
      case "ArrowDown": case "PageDown": case " ": dir = 1; break;
      case "ArrowUp": case "PageUp": dir = -1; break;
      case "Home": e.preventDefault(); hopToNode(0); return;
      case "End": e.preventDefault(); hopToNode(TIMELINE.length - 1); return;
    }
    if (dir !== 0) {
      e.preventDefault();
      tryHop(dir);
    }
  });

  // Touch: native scroll flows during the gesture (so iOS Safari toolbars
  // can collapse), then on release we snap to next/previous/same node —
  // mirroring the wheel hijack but using native scroll for the gesture.
  let touchStartY = null;
  let touchStartNodeIdx = 0;

  function resetTouch() { touchStartY = null; }

  window.addEventListener("touchstart", (e) => {
    if (modalIsOpen() || isInteractiveTarget(e)) return;
    if (e.touches.length > 1) return resetTouch(); // ignore multi-touch
    touchStartY = e.touches[0].clientY;
    touchStartNodeIdx = inferNodeIndexFromScroll();
  }, { passive: true });

  window.addEventListener("touchend", (e) => {
    if (modalIsOpen() || touchStartY === null) return;
    const dy = touchStartY - e.changedTouches[0].clientY;
    resetTouch();

    if (Math.abs(dy) >= TOUCH_SWIPE_THRESHOLD) {
      hopToNode(touchStartNodeIdx + Math.sign(dy));
    } else {
      // Tap or near-tap — pull back to the starting node so we never
      // settle between snap points.
      hopToNode(touchStartNodeIdx);
    }
  });

  window.addEventListener("touchcancel", resetTouch);
}

// ── Compact moon transform ─────────────────────────────────────────────────

// 0 at the top of the page, 1 once we've scrolled a full viewport (i.e. past
// the hero into the first memory section). Held at 1 thereafter. Uses the
// stable small-viewport height so the transition feel doesn't change as
// iOS Safari's toolbars collapse.
function compactProgress() {
  return Math.max(0, Math.min(1, window.scrollY / getSmallVH()));
}

// Chevron on every node except the last; heart on the last node. The CSS
// `.at-last` class drives both the icon crossfade and the swap from the
// bounce keyframe to the heartbeat keyframe. We flip at the midpoint
// between the last-1 and last snap positions so the swap happens during
// the hop animation rather than abruptly at one end.
function applyScrollHintIcon() {
  const el = document.querySelector(".scroll-hint");
  if (!el) return;
  const lastIdx = TIMELINE.length - 1;
  if (lastIdx <= 0) {
    el.classList.add("at-last");
    return;
  }
  const lastSnapY = snapPositionForNode(lastIdx);
  const prevSnapY = snapPositionForNode(lastIdx - 1);
  const midpoint = (prevSnapY + lastSnapY) / 2;
  el.classList.toggle("at-last", window.scrollY >= midpoint);
}

function applyMoonTransform() {
  const t = compactProgress();
  const isMobile = window.innerWidth < config.mobileBreakpoint;
  const targetScale = isMobile ? config.compactScaleMobile : config.compactScale;
  const targetYVh = isMobile ? config.compactYOffsetVhMobile : config.compactYOffsetVh;
  const scale = 1 + t * (targetScale - 1);
  const yVh = t * targetYVh;
  const el = document.getElementById("moon");
  if (el) el.style.transform = `translateY(${yVh}vh) scale(${scale})`;
}

// ── Timeline ───────────────────────────────────────────────────────────────

// Interpolate phase fraction along the SHORT FORWARD arc of the lunar cycle:
// always advances (never reverses), and travels just enough to reach phaseB.
//   phaseA=0.88, phaseB=0.41  →  diff = 0.53, path 0.88 → 0 → 0.41
//   phaseA=0.10, phaseB=0.50  →  diff = 0.40, path 0.10 → 0.50
//   phaseA=0.50, phaseB=0.10  →  diff = 0.60, path 0.50 → 0 → 0.10
function shortestForwardPhase(phaseA, phaseB, t) {
  let diff = phaseB - phaseA;
  if (diff < 0) diff += 1;
  return (phaseA + diff * t) % 1;
}

// Snap-point layout (with N timeline nodes):
//   scrollY = 0                                    → node 0  (rendered by .hero)
//   scrollY = heroEnd                              → node 1  (timeline-node 0)
//   scrollY = heroEnd + sectionHeight              → node 2
//   scrollY = heroEnd + (k-1) · sectionHeight      → node k     (k ≥ 1)
//
// Between snap points: date is lerped linearly (so the eyebrow reads
// naturally), but phase is interpolated independently along the shortest
// forward arc — so the moon doesn't strobe through every cycle between
// dates that are years apart.
function timelineStateFromScroll() {
  const sy = window.scrollY;
  const vh = getSmallVH();
  const heroEnd = vh;
  const sectionPx = (vh * config.sectionHeightVH) / 100;
  const last = TIMELINE.length - 1;

  let i, t;
  if (sy <= 0) {
    i = 0;
    t = 0;
  } else if (sy <= heroEnd) {
    i = 0;
    t = sy / heroEnd;
  } else {
    const idx = (sy - heroEnd) / sectionPx;
    const segment = Math.floor(idx);
    t = idx - segment;
    i = 1 + segment;
  }

  if (i >= last) {
    const date = new Date(TIMELINE[last].date);
    return { date, phaseFrac: moonPhaseFraction(date) };
  }

  const dateA = new Date(TIMELINE[i].date);
  const dateB = new Date(TIMELINE[i + 1].date);
  const date = new Date(dateA.getTime() + t * (dateB.getTime() - dateA.getTime()));

  const phaseA = moonPhaseFraction(dateA);
  const phaseB = moonPhaseFraction(dateB);
  const phaseFrac = shortestForwardPhase(phaseA, phaseB, t);

  return { date, phaseFrac };
}

// ── Debug GUI ──────────────────────────────────────────────────────────────

function intToHex(n) {
  return "#" + n.toString(16).padStart(6, "0");
}

function buildDebugGUI(refresh) {
  const gui = new GUI({ title: "Moon · debug", width: 280 });

  const fBirth = gui.addFolder("Birth event");
  fBirth.add(config, "birthDateUTC").name("date (UTC)").onChange(refresh);
  fBirth.add(config, "observerLat", -90, 90, 0.0001).name("latitude").onChange(refresh);
  fBirth.add(config, "observerLon", -180, 180, 0.0001).name("longitude (unused)");

  const fCamera = gui.addFolder("Camera");
  fCamera.add(config, "moonScreenFraction", 0.1, 1.0, 0.005)
    .name("desktop w-frac").onChange(refresh);
  fCamera.add(config, "moonScreenFractionMobile", 0.1, 1.0, 0.005)
    .name("mobile w-frac").onChange(refresh);
  fCamera.add(config, "moonMaxHeightFraction", 0.2, 1.0, 0.01)
    .name("max h-frac").onChange(refresh);
  fCamera.add(config, "mobileBreakpoint", 320, 1200, 4)
    .name("mobile breakpoint").onChange(refresh);
  fCamera.add(config, "fov", 10, 90, 1).onChange(refresh);

  const fCompact = gui.addFolder("Compact (past hero)");
  fCompact.add(config, "compactScale", 0.1, 1.0, 0.01)
    .name("desktop scale").onChange(refresh);
  fCompact.add(config, "compactScaleMobile", 0.1, 1.0, 0.01)
    .name("mobile scale").onChange(refresh);
  fCompact.add(config, "compactYOffsetVh", -50, 50, 1)
    .name("desktop y (vh)").onChange(refresh);
  fCompact.add(config, "compactYOffsetVhMobile", -50, 50, 1)
    .name("mobile y (vh)").onChange(refresh);

  const fLight = gui.addFolder("Lighting");
  fLight.add(config, "sunIntensity", 0, 8, 0.05).name("sun").onChange(refresh);
  fLight.add(config, "ambientIntensity", 0, 0.1, 0.001).name("ambient").onChange(refresh);

  const fSurface = gui.addFolder("Surface");
  fSurface.add(config, "bumpScale", 0, 1.5, 0.01).name("bump").onChange(refresh);

  const fStyle = gui.addFolder("Stylization");
  const tintProxy = { tint: intToHex(config.moonTint) };
  fStyle.addColor(tintProxy, "tint").name("tint").onChange((v) => {
    config.moonTint = parseInt(v.replace("#", ""), 16);
    refresh();
  });
  fStyle.add(config, "grainScale", 50, 1000, 10).name("grain freq").onChange(refresh);
  fStyle.add(config, "grainAmount", 0, 1, 0.01).name("grain amount").onChange(refresh);
  fStyle.add(config, "contrast", 0.5, 3.0, 0.01).onChange(refresh);

  const fTimeline = gui.addFolder("Timeline");
  fTimeline.add(config, "sectionHeightVH", 100, 600, 10)
    .name("section height (vh)").onChange(refresh);

  const fStars = gui.addFolder("Stars");
  fStars.add(starsConfig, "parallaxFactor", 0, 0.5, 0.01)
    .name("parallax").onChange(() => {
      // Field height depends on parallax factor; rebuild and re-apply offset.
      renderStarfield();
      applyStarsParallax(window.scrollY);
    });
  fStars.add(starsConfig, "bgStarCount", 50, 600, 10)
    .name("star count").onChange(() => {
      renderStarfield();
      applyStarsParallax(window.scrollY);
    });

  const fOrient = gui.addFolder("Orientation");
  const orientProxy = {
    auto: config.manualTiltDeg === null,
    tilt: config.manualTiltDeg ?? tiltDegFromLatitude(config.observerLat),
  };
  fOrient.add(orientProxy, "auto").name("auto from latitude").onChange((v) => {
    config.manualTiltDeg = v ? null : orientProxy.tilt;
    refresh();
  });
  fOrient.add(orientProxy, "tilt", -180, 180, 1).name("tilt (deg)").onChange((v) => {
    orientProxy.tilt = v;
    if (!orientProxy.auto) {
      config.manualTiltDeg = v;
      refresh();
    }
  });

  return gui;
}

function shouldShowDebugGUI() {
  // __DEV__ is replaced at build time by esbuild's define. In production
  // builds it's the literal `false`, so this whole function returns early
  // and the rest of the GUI logic gets dead-code-eliminated.
  if (!__DEV__) return false;
  const host = location.hostname;
  const isLocal = host === "localhost" || host === "127.0.0.1" || host === "";
  const flagged = new URLSearchParams(location.search).has("debug");
  return isLocal || flagged;
}

// ── Init ───────────────────────────────────────────────────────────────────

function applyTimelineLength() {
  // svh, not dvh — section heights must stay constant across iOS Safari
  // toolbar transitions, otherwise the document's scroll structure shifts
  // and the user gets stranded between snap points. See Lever A in
  // README/notes.
  document.documentElement.style.setProperty(
    "--section-height",
    `${config.sectionHeightVH}svh`,
  );
}

function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Deterministic pseudo-random in [0, 1). Stable across re-renders so a node's
// rotations don't jitter when the page is resized.
function seedRandom(seed) {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function renderCollage(images, nodeIdx, isMobile) {
  const count = Math.min(4, images.length);
  if (count === 0) return "";
  const variant = isMobile ? "mobile" : "desktop";
  const layout = COLLAGE_LAYOUTS[variant][count];
  const items = images
    .slice(0, count)
    .map((src, i) => {
      const pos = layout[i];
      const rot = ((seedRandom(nodeIdx * 17 + i * 3 + 1) - 0.5) * 14).toFixed(1);
      // const z = Math.floor(seedRandom(nodeIdx * 23 + i * 5 + 11) * 10);
      const z = nodeIdx;
      const inner = src
        ? `<img src="${escapeHtml(src)}" alt="" />`
        : `<span class="collage-ph-label">Photo ${i + 1}</span>`;
      return `
        <div class="collage-image"
             style="left:${pos.x}%;top:${pos.y}%;width:${pos.w}%;height:${pos.h}%;--rotation:${rot}deg;z-index:${z};">
          ${inner}
        </div>`;
    })
    .join("");
  return `<div class="node-collage" data-count="${count}">${items}</div>`;
}

// ── Loading overlay ────────────────────────────────────────────────────────

let loadingHidden = false;
function hideLoadingOverlay() {
  if (loadingHidden) return;
  loadingHidden = true;
  const el = document.getElementById("loading");
  if (!el) return;
  el.classList.add("fade-out");
  setTimeout(() => el.remove(), 700);
}

// ── Image modal ────────────────────────────────────────────────────────────

let imageModalEl = null;

function modalIsOpen() {
  return !!(imageModalEl && imageModalEl.classList.contains("open"));
}

function setupImageModal() {
  const modal = document.createElement("div");
  modal.className = "image-modal";
  modal.setAttribute("aria-hidden", "true");
  modal.innerHTML = `
    <button class="image-modal-close" type="button" aria-label="Close">×</button>
    <img alt="" />
  `;
  document.body.appendChild(modal);
  imageModalEl = modal;

  const modalImg = modal.querySelector("img");
  const closeBtn = modal.querySelector(".image-modal-close");

  function open(src) {
    modalImg.src = src;
    modal.classList.add("open");
    modal.setAttribute("aria-hidden", "false");
  }
  function close() {
    modal.classList.remove("open");
    modal.setAttribute("aria-hidden", "true");
  }

  closeBtn.addEventListener("click", close);
  modal.addEventListener("click", (e) => {
    // Close on backdrop click; image and close button stop here naturally.
    if (e.target === modal) close();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modalIsOpen()) close();
  });

  // Touch behavior on the modal (mobile):
  //   1. Block background page scroll by preventDefault'ing touchmove
  //      while the modal is open. The modal has pointer-events:none when
  //      closed, so these handlers don't fire then — native scroll still
  //      works on the page itself.
  //   2. Any meaningful single-finger swipe closes the modal.
  // Multi-finger gestures (pinch, etc.) are left to the browser.
  const SWIPE_CLOSE_THRESHOLD_PX = 30;
  let touchStartX = null;
  let touchStartY = null;

  modal.addEventListener("touchstart", (e) => {
    if (e.touches.length > 1) {
      touchStartX = touchStartY = null;
      return;
    }
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  }, { passive: true });

  modal.addEventListener("touchmove", (e) => {
    if (e.touches.length > 1) return; // allow pinch / multi-finger
    e.preventDefault();
  }, { passive: false });

  modal.addEventListener("touchend", (e) => {
    if (touchStartX === null) return;
    const dx = Math.abs(touchStartX - e.changedTouches[0].clientX);
    const dy = Math.abs(touchStartY - e.changedTouches[0].clientY);
    touchStartX = touchStartY = null;
    if (dx >= SWIPE_CLOSE_THRESHOLD_PX || dy >= SWIPE_CLOSE_THRESHOLD_PX) close();
  });

  modal.addEventListener("touchcancel", () => {
    touchStartX = touchStartY = null;
  });

  // Click delegation for collage images. Non-passive so we can stop the
  // event from bubbling into navigation handlers.
  document.addEventListener("click", (e) => {
    const card = e.target.closest(".collage-image");
    if (!card) return;
    const realImg = card.querySelector("img");
    if (!realImg || !realImg.getAttribute("src")) return; // placeholder

    // Bring this card to the front of its collage stack.
    const collage = card.closest(".node-collage");
    if (collage) {
      collage.querySelectorAll(".collage-image.front").forEach((el) => {
        if (el !== card) el.classList.remove("front");
      });
      card.classList.add("front");
    }

    open(realImg.src);
  });
}

// IntersectionObserver that adds `.in-view` to each timeline-node the first
// time it scrolls into the viewport, triggering the card entrance animation.
// Reset (.disconnect/re-attach) on every render so it picks up the freshly-
// generated DOM after resize-driven re-renders.
let timelineObserver = null;

function setupTimelineObserver() {
  if (timelineObserver) timelineObserver.disconnect();
  const nodes = document.querySelectorAll(".timeline-node");
  if (typeof IntersectionObserver === "undefined") {
    nodes.forEach((n) => n.classList.add("in-view"));
    return;
  }
  timelineObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add("in-view");
          timelineObserver.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.15 },
  );
  nodes.forEach((n) => timelineObserver.observe(n));
}

// Render one <section class="timeline-node"> per node in TIMELINE, skipping
// node 0 (the hero already represents it). Idempotent: safe to call again.
function renderTimelineNodes() {
  const container = document.querySelector(".timeline");
  if (!container) return;
  const isMobile = window.innerWidth < config.mobileBreakpoint;
  container.innerHTML = TIMELINE.slice(1)
    .map((node, i) => {
      const collage = renderCollage(node.images || [], i + 1, isMobile);
      return `
        <section class="timeline-node">
          ${collage}
          <div class="node-content">
            <p class="node-description">
              ${node.description.map(d => escapeHtml(d)).join('<br />')}
            </p>
          </div>
        </section>
      `;
    })
    .join("");
  setupTimelineObserver();
}

function init() {
  const container = document.getElementById("moon");
  const ctx = buildScene(container);
  applyStaticConfig(ctx);
  applyTimelineLength();
  renderTimelineNodes();
  renderStarfield();
  applyStarsParallax(window.scrollY);
  applyMoonTransform();
  applyScrollHintIcon();
  applyAmbientTint();
  applyDateState(ctx, timelineStateFromScroll());
  setupImageModal();
  preloadAroundCurrent();

  // Safety net: if the texture load callback never fires (network error,
  // cached oddities, etc.), the overlay still goes away.
  setTimeout(hideLoadingOverlay, 5000);

  // The moon canvas is `position: fixed; height: 100dvh`, so its CSS size
  // changes mid-iOS-toolbar-transition (every animation frame, not just at
  // the end). `window.resize` doesn't fire fast enough to keep the WebGL
  // renderer's internal pixel buffer in sync with the CSS box, which
  // causes visual stretching during the transition. ResizeObserver fires
  // on every actual element size change, so this keeps the renderer and
  // camera glued to the container perfectly.
  if (typeof ResizeObserver !== "undefined") {
    const moonRO = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      const h = Math.round(entry.contentRect.height);
      if (!w || !h) return;
      // setSize() resets canvas.width/height which per HTML5 spec clears
      // the canvas. If we wait for the next requestAnimationFrame tick
      // to re-render, the canvas is briefly blank — visible as a flicker
      // during multi-fire iOS toolbar transitions. Render synchronously
      // here so the new framebuffer is filled before the browser paints.
      ctx.renderer.setSize(w, h);
      ctx.camera.aspect = w / h;
      ctx.camera.position.z = cameraDistanceForViewport(config.fov, w, h);
      ctx.camera.updateProjectionMatrix();
      applyMoonTransform();
      ctx.renderer.render(ctx.scene, ctx.camera);
    });
    moonRO.observe(container);
  }

  let starfieldResizeTimer = null;
  // Tracks the mobile-vs-desktop state across resize events so we only
  // re-render the timeline DOM when the breakpoint actually changes.
  // iOS Safari toolbar transitions fire `resize` (height changes) but
  // don't change width — without this guard, every transition would
  // recreate the timeline and replay the entrance animations.
  let lastIsMobile = window.innerWidth < config.mobileBreakpoint;
  function resize() {
    // Renderer/camera updates are handled by the ResizeObserver above.
    // This handler only runs the things that should react to a more
    // global "viewport changed" signal — starfield rebuild, breakpoint
    // re-render — and does so with a debounce so dragging window edges
    // doesn't thrash.
    clearTimeout(starfieldResizeTimer);
    starfieldResizeTimer = setTimeout(() => {
      renderStarfield();
      applyStarsParallax(window.scrollY);
      const isMobile = window.innerWidth < config.mobileBreakpoint;
      if (isMobile !== lastIsMobile) {
        lastIsMobile = isMobile;
        renderTimelineNodes();
      }
    }, 200);
  }
  window.addEventListener("resize", resize);

  bindNavigation();

  // Drive the timeline from scroll. Polled in the render loop so we stay
  // synced to vsync and avoid extra event overhead.
  let lastScrollY = -1;
  let lastNodeIdxForPreload = -1;
  function loop() {
    const sy = window.scrollY;
    if (sy !== lastScrollY) {
      applyDateState(ctx, timelineStateFromScroll());
      applyMoonTransform();
      applyStarsParallax(sy);
      applyScrollHintIcon();
      applyAmbientTint();
      // Trigger preload only when the inferred current node changes,
      // so we don't spam Image() constructors on every scroll pixel.
      const idx = inferNodeIndexFromScroll();
      if (idx !== lastNodeIdxForPreload) {
        lastNodeIdxForPreload = idx;
        preloadAroundCurrent();
      }
      lastScrollY = sy;
    }
    ctx.renderer.render(ctx.scene, ctx.camera);
    requestAnimationFrame(loop);
  }
  loop();

  // Expose for live tweaking from the browser console:
  //   window.momgift.config.sunIntensity = 4; window.momgift.refresh();
  window.momgift = {
    config,
    ctx,
    refresh: () => {
      ctx.sun.intensity = config.sunIntensity;
      ctx.scene.children.find((c) => c.isAmbientLight).intensity = config.ambientIntensity;
      ctx.moon.material.bumpScale = config.bumpScale;
      ctx.moon.material.color.set(config.moonTint);
      const u = ctx.moon.material.userData.styleUniforms;
      u.uGrainScale.value = config.grainScale;
      u.uGrainAmount.value = config.grainAmount;
      u.uContrast.value = config.contrast;
      ctx.camera.fov = config.fov;
      ctx.camera.position.z = cameraDistanceForViewport(
        config.fov,
        window.innerWidth,
        window.innerHeight,
      );
      ctx.camera.updateProjectionMatrix();
      applyStaticConfig(ctx);
      applyTimelineLength();
      // Section height may have changed; re-snap to the current node so the
      // moon doesn't end up stranded between two snap positions.
      window.scrollTo({ top: snapPositionForNode(currentNodeIndex), behavior: "auto" });
      applyMoonTransform();
      applyDateState(ctx, timelineStateFromScroll());
    },
    hopToNode,
    get currentNode() { return currentNodeIndex; },
  };

  if (shouldShowDebugGUI()) {
    buildDebugGUI(window.momgift.refresh);
  }
}

init();
